import {useFlux, useFluxListener} from '@nlabs/arkhamjs-utils-react';
import {Button} from '@nlabs/gothamjs/components';
import {ArrowRight, RefreshCw} from '@nlabs/gothamjs/icons';
import {Link} from '@nlabs/gothamjs/router';
import {formatCredits} from '@reactorbox/shared';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {ACCOUNT_EVENTS, createAccountActions} from '../../state/accountActions.js';
import {glassPanel, mutedText, pageHeading, secondaryButton} from '../../theme/classes.js';
import {MembershipPanel} from '../MembershipPanel/MembershipPanel.js';

import type {ReactElement} from 'react';
import type {AccountDetails} from '../../state/accountActions.js';

export const AccountPage = (): ReactElement => {
  const flux = useFlux();
  const actions = useMemo(() => createAccountActions(flux), [flux]);
  const [account, setAccount] = useState<AccountDetails>();
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  useFluxListener(ACCOUNT_EVENTS.PENDING, useCallback(() => {
    setBusy(true);
    setError('');
  }, []));
  useFluxListener(ACCOUNT_EVENTS.CHANGED, useCallback(() => {
    setAccount(flux.getState('account.data') as AccountDetails);
    setBusy(false);
  }, [flux]));
  useFluxListener(ACCOUNT_EVENTS.ERROR, useCallback(() => {
    setAccount(undefined);
    setError(flux.getState('account.error', '') as string);
    setBusy(false);
  }, [flux]));
  useEffect(() => {
    void actions.load();
    const refresh = (): void => {
      void actions.load();
    };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [actions]);
  return <div className="mx-auto max-w-5xl space-y-8 px-5 py-12 sm:px-8 sm:py-20">
    <header className="flex flex-wrap items-start justify-between gap-5"><div><h1 className={pageHeading}>Your account<span className="text-violet-300">.</span></h1><p className={`${mutedText} mt-4`}>Your details, generation balance, and membership in one place.</p></div><Button className={secondaryButton} disabled={busy} icon={<RefreshCw size={16} />} onClick={() => void actions.load()}>Refresh account</Button></header>
    {error && <p className="rounded-2xl border border-rose-300/20 bg-rose-300/10 p-5 text-rose-100" role="alert">{error}</p>}
    {busy && !account && <p className={mutedText} role="status">Loading your account…</p>}
    {account && <>
      <section aria-labelledby="account-details-heading" className={`${glassPanel} p-7 sm:p-9`}><h2 className="text-2xl text-violet-200" id="account-details-heading">Account details</h2><dl className="mt-7 grid gap-6 sm:grid-cols-2"><div><dt className="text-sm text-white/50">Name</dt><dd className="mt-2 break-words text-lg">{account.user.name}</dd></div><div><dt className="text-sm text-white/50">Email</dt><dd className="mt-2 break-words text-lg">{account.user.email}</dd></div></dl><Link className="mt-7 inline-flex text-sm text-sky-200" to={`/forgot-password?email=${encodeURIComponent(account.user.email)}`}>Reset password</Link></section>
      <section aria-labelledby="account-credits-heading" className={`${glassPanel} p-7 sm:p-9`}><h2 className="text-2xl text-violet-200" id="account-credits-heading">Account credits</h2>{account.credits ? <dl className="mt-7 grid gap-6 sm:grid-cols-3">{[
        {label: 'Available', value: account.credits.available},
        {label: 'Reserved', value: account.credits.reserved},
        {label: 'Spent', value: account.credits.spent}
      ].map(({label, value}) => <div key={label}><dt className="text-sm text-white/50">{label}</dt><dd className="mt-3 text-4xl tabular-nums">{formatCredits(value)}</dd></div>)}</dl> : <><p className="mt-6 text-xl text-white/85">Credit balance unavailable</p><p className={`${mutedText} mt-3`}>Your credit balance could not be loaded. Refresh your account to try again.</p></>}<Link className="mt-7 inline-flex items-center gap-2 text-sky-200" to="/billing">View studio usage <ArrowRight size={16} /></Link></section>
      <MembershipPanel available={account.upgradeStatus === 'available'} busy={busy} hasSubscription={account.hasSubscription} membership={account.membership} onBilling={(plan) => void actions.openBilling(plan)} plan={account.plan} />
    </>}
  </div>;
};
